import './flow-invite-upsell.css'

import flowRecordImg from './media/flow-record.png'
import flowDashboardImg from './media/flow-dashboard.png'

import React from 'react'
import { IonButton, IonContent, IonIcon, IonPage, IonRouterLink, useIonAlert, useIonRouter } from '@ionic/react'
import { analyticsOutline, giftOutline, peopleOutline, sparklesOutline, statsChartOutline, stopwatchOutline } from 'ionicons/icons'
import { useLocation } from 'react-router-dom'

import Header from './Header'
import { useMyGame, useSection, useUserHasPro } from './data'
import { DisplayUpgradeOffer, PlanName } from './Plan'
import { shareUrl } from './shareUrl'
import { getUrl } from './util'
import { getGameSharePreviewUrl } from './previewShareUrl'
import { MoreInfoLink } from './MoreInfoLink'
import tutorialRecordTurnovers from './media/tutorial-record-turnovers.mp4'

type LocationData = {
	hash: string
	key: string
	pathname: string
	search: string
	state: null
}

type UseLocationData = LocationData | {
	action: string
	location: LocationData
}

const FlowInviteUpsell = () => {
	const game = useMyGame()
	const section = useSection()
	const userHasPro = useUserHasPro()
	const locationData: UseLocationData = useLocation()
	const hasGameContext = !!game?.id
	const { pathname, search } = ('location' in locationData ? locationData.location : locationData)
	const backHref = pathname.startsWith('/help/')
		? '/help'
		: `/games${search || ''}`
	const [ presentAlert ] = useIonAlert()
	const router = useIonRouter()

	const recordLink = hasGameContext ? getUrl({
		page: `/games/${game.id}/record-flows`,
		section,
	}) : null
	const shareLink = hasGameContext ? getGameSharePreviewUrl({
		kind: 'record-flows',
		gameId: game.id,
		section,
	}) : null

	return (
		<IonPage>
			<IonContent>
				<div className="flow-invite-upsell">
                    <section className="flow-invite-hero">
                        <h1>Flow Maps</h1>
                        <p>
                            Available with any <PlanName /> subscription.
                        </p>
                    </section>

                    <FlowInviteGallery />

                    <section className="flow-invite-copy">
                        <div className="flow-invite-copy-content">

                            <div className="flow-invite-point">
                                <IonIcon icon={sparklesOutline} />
                                <div>
                                    <strong>Easy Recording</strong>
                                    <p>
                                        Anyone can record. A simple tap on turnover or goal
                                        is all that's required.
                                    </p>
                                </div>
                            </div>
                            <div className="flow-invite-point">
                                <IonIcon icon={analyticsOutline} />
                                <div>
                                    <strong>Reveal Ball Movement Traps</strong>
                                    <p>
                                        See where the ball is being lost and won,
                                        with a focus on Midcourt transition & Attack Entry conversion.
                                    </p>
                                </div>
                            </div>
                            <div className="flow-invite-point">
                                <IonIcon icon={stopwatchOutline} />
                                <div>
                                    <strong>Real-Time Insight</strong>
                                    <p>
                                        Live stats allow coaches to respond to on-court dynamics as they happen.
                                    </p>
                                </div>
                            </div>
                            <div className="flow-invite-point">
                                <IonIcon icon={statsChartOutline} />
                                <div>
                                    <strong>See What Happened When</strong>
                                    <p>
                                        Time-based Dominance analysis reveals connections between scoring
                                        and control of each court third.
                                    </p>
                                </div>
                            </div>
                        </div>

                    </section>

                    <section className="flow-invite-video">
                        <div className="video-container">
                            <video playsInline autoPlay muted loop>
                                <source src={tutorialRecordTurnovers} type="video/mp4" />
                            </video>
                        </div>
                    </section>

                    <section className="flow-invite-cta">
                        {
                            userHasPro ? (
                                <div className="wtf">
                                    <p>
                                        You have <PlanName /> already!
                                    </p>
									{
										shareLink ? (
											<>
												<IonButton
													expand="block"
													size="large"
													onClick={() => recordLink && router.push(recordLink, 'forward', 'push')}
												>
													Record {game?.opponent ? `Game vs ${game.opponent}` : 'this game'}
												</IonButton>

												<IonButton
													expand="block"
													size="large"
													onClick={() => {
														void shareUrl({
															url: shareLink,
															title: `Record game stats for ${game?.opponent || 'this game'}`,
															presentAlert,
														}).catch(error => {
															console.error('Failed to share flow invite', error)
															presentAlert({
																header: 'Could not share invite',
																message: 'Please try again.',
																buttons: [ 'OK' ],
															})
														})
													}}
												>
													Share Invite Link
												</IonButton>
											</>
										) : (
											<>
												<p>
													Record stats for a game (or invite someone else to)
                                                    by tapping the three-dot menu on any game in the <b>Games</b> tab.
												</p>
												<IonButton
													expand="block"
                                                    fill="outline"
													routerLink="/games"
												>
													Games
												</IonButton>
											</>
										)
									}
                                </div>
                            ) : (
                                <DisplayUpgradeOffer />
                            )
                        }
                    </section>

                    <section className="flow-invite-copy">
                        <p className="flow-invite-copy-header">
                            Subscribers can:
                        </p>
                        <div className="flow-invite-copy-content">
                            <div className="flow-invite-point">
                                <IonIcon icon={peopleOutline} />
                                <div>
                                    <strong>Invite Anyone to Record</strong>
                                    <p>
                                        Send a link to record your game in their device's web browser &mdash; no AutoRoster required.
                                    </p>
                                </div>
                            </div>
                            <div className="flow-invite-point">
                                <IonIcon icon={giftOutline} />
                                <div>
                                    <strong>Record Any Game</strong>
                                    <p>
                                        Record Flow Maps for other AutoRoster users &mdash; even if they don't have a subscription.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section>
                        <p className="flow-invite-copy-header">
                            <MoreInfoLink helpSection="insight" text="How Flow Maps work..." />
                        </p>
                    </section>

                </div>
            </IonContent>
            <Header backHref={backHref} />
        </IonPage>
    )
}

type FlowInviteGalleryProps = {
	routerLink?: string
}

export const FlowInviteGallery = (props: FlowInviteGalleryProps) => {
	const gallery = (
		<section className="flow-invite-gallery" aria-label="Flow recording previews">
			<article className="flow-shot flow-shot-recorder">
				<img src={flowRecordImg} alt="Flow Map example" />
			</article>

			<article className="flow-shot flow-shot-map">
				<img src={flowDashboardImg} alt="Flow Map example" />
			</article>
		</section>
	)

	if (!props.routerLink) {
		return gallery
	}

	return (
		<IonRouterLink routerLink={props.routerLink} style={{ display: 'block' }}>
			{gallery}
		</IonRouterLink>
	)
}

export default FlowInviteUpsell
